import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActiveAlert, SavedSearchAlertType } from '../../types/savedSearch.types';

interface DismissedAlert {
  searchId: number;
  countAtDismiss: number;
}

const STORAGE_KEY = 'dismissed_alerts';

export const ALERT_TYPE_PRIORITY: Record<SavedSearchAlertType, number> = {
  critical: 3,
  warning: 2,
  info: 1,
};

const readDismissed = (): DismissedAlert[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const writeDismissed = (dismissed: DismissedAlert[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
  } catch {
    console.error('Failed to save dismissed alerts');
  }
};

/** Alertes à afficher (compteur > 0, non ignorées), triées par gravité puis volume. */
export function useVisibleAlerts(alerts: ActiveAlert[]) {
  const [dismissed, setDismissed] = useState<DismissedAlert[]>(() => readDismissed());
  const prevCountsRef = useRef<Record<number, number>>({});

  useEffect(() => {
    const prev = prevCountsRef.current;
    const byId = new Map(alerts.map((a) => [a.searchId, a]));
    let changed = false;
    const next = dismissed.filter((d) => {
      const alert = byId.get(d.searchId);
      if (!alert) return true;
      const prevCount = prev[d.searchId];
      if (prevCount !== undefined && prevCount !== alert.count) {
        changed = true;
        return false;
      }
      return true;
    });
    if (changed) {
      setDismissed(next);
      writeDismissed(next);
    }
    const counts: Record<number, number> = {};
    alerts.forEach((a) => {
      counts[a.searchId] = a.count;
    });
    prevCountsRef.current = counts;
  }, [alerts, dismissed]);

  const visible = useMemo(
    () =>
      alerts
        .filter((a) => {
          if (a.count === 0) return false;
          const d = dismissed.find((x) => x.searchId === a.searchId);
          return !(d && d.countAtDismiss === a.count);
        })
        .sort((a, b) => {
          const byType = (ALERT_TYPE_PRIORITY[b.type] || 0) - (ALERT_TYPE_PRIORITY[a.type] || 0);
          return byType !== 0 ? byType : b.count - a.count;
        }),
    [alerts, dismissed]
  );

  const dismiss = useCallback(
    (searchId: number, count: number) => {
      const next = [
        ...dismissed.filter((d) => d.searchId !== searchId),
        { searchId, countAtDismiss: count },
      ];
      setDismissed(next);
      writeDismissed(next);
    },
    [dismissed]
  );

  return { visible, dismiss };
}
